import { FontItem } from '../types';

const DB_NAME = 'hzhandwriting';
const DB_VERSION = 1;
const STORE_NAME = 'fonts';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export interface StoredFont {
  id: string;
  name: string;
  desc: string;
  family: string;
  isCustom: boolean;
  blob: Blob;
}

/**
 * Save a font file as a Blob in IndexedDB.
 */
export async function saveFont(font: FontItem, blob: Blob): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const record: StoredFont = {
      id: font.id,
      name: font.name,
      desc: font.desc,
      family: font.family,
      isCustom: font.isCustom,
      blob,
    };
    store.put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Load all stored font metadata (without blobs) from IndexedDB.
 */
export async function loadFontList(): Promise<FontItem[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const records = request.result as StoredFont[];
      const items: FontItem[] = records.map((r) => ({
        id: r.id,
        name: r.name,
        desc: r.desc,
        family: r.family,
        isCustom: r.isCustom,
      }));
      resolve(items);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Load a single font's Blob from IndexedDB by id.
 */
export async function loadFontBlob(fontId: string): Promise<Blob | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(fontId);
    request.onsuccess = () => {
      const record = request.result as StoredFont | undefined;
      resolve(record?.blob ?? null);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a font from IndexedDB.
 */
export async function deleteFont(fontId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(fontId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
